import { MEDIA_DEVICE_STATUSES, TABLE_TYPES } from "@/helpers/constants";

export default {
  /**
   * Get domain for user email
   * @param state {Object}
   * @return {String|null}
   */
  userDomain: state => {
    return state.user ? state.user.user_email.split("@")[1].toLowerCase() : null;
  },

  /**
   * Get user email
   * @param state {Object}
   * @return {String|null}
   */
  userEmail: state => {
    return state.user ? state.user.user_email.toLowerCase() : null;
  },

  /**
   * Get User object
   * @param state {Object}
   * @param getters {Object}
   * @param rootState
   * @param rootGetters
   * @return {function(*): *}
   */
  getUser: (state, getters, rootState, rootGetters) => userUUID => {
    return rootGetters["room/roomState"].find(user => user.userUUID === userUUID) || {};
  },

  /**
   * Get user full name
   * @return {function(*): string}
   */
  getUserFullName: () => user => {
    return [
      user.profile && user.profile.firstName ? user.profile.firstName : user.firstName || "",
      user.profile && user.profile.lastName ? user.profile.lastName : user.lastName || ""
    ].join(" ");
  },

  /**
   * Get user full name
   * @param state {Object}
   * @param getters {Object}
   * @return {String|null}
   */
  userFullName: (state, getters) => {
    return getters.getUserFullName(getters.getUser(state.user.user_uuid));
  },

  /**
   * Allowed roles for current access type
   * @param state {Object}
   * @param getters {Object}
   * @return {null|Object}
   */
  allowedRoles: (state, getters, rootState) => {
    return rootState.event.config.accessConfig.availableRoles[rootState.event.context.EVENT_ACCESS_TYPE] || null;
  },

  /**
   * User Assignment - it's user's domain/email
   * @param state {Object}
   * @param getters {Object}
   * @return {T|null}
   */
  userAssignment: (state, getters, rootState) => {
    let email = state.user.user_email.toLowerCase();
    let domain = getters.userDomain.toLowerCase();
    return (
      rootState.room.roomAssignments.find(assignment => {
        return assignment.value === email || assignment.value === domain;
      }) || null
    );
  },

  /**
   * Check that user can accept video call invites
   * @param rootState {Object}
   * @return {Function}
   */
  canUserAcceptVideoCallInvites: (state, getters, rootState) => user => {
    if (!user) {
      return !!rootState.calls.devices.isReady;
    } else {
      return user.isOnline && user.mediaDeviceStatus >= MEDIA_DEVICE_STATUSES.ALLOW;
    }
  },

  /**
   * Get the current AR user from the room state
   * @param state {Object}
   * @param getters {Object}
   * @return {Function}
   */
  currentUser: (state, getters, rootState, rootGetters) => {
    return rootGetters["room/roomState"].find(user => user.userUUID === state.user.user_uuid);
  },

  /**
   * Get current user profile
   * @param state {Object}
   * @param getters {Object}
   * @return {Object}
   */
  currentUserProfile: (state, getters) => {
    const person = getters.currentUser;
    if (!person || !person.profile || !Object.keys(person.profile).length) {
      return state.user.profile;
    }
    return person.profile;
  },

  /**
   * Check if the current user has netwoking capability
   * @return {Object}
   */
  currentUserPermissions: (state, getters, rootState, rootGetters) => {
    let canNetwork = rootGetters["tables/userTable"]
      ? rootGetters["tables/userTable"].TABLE_TYPE !== TABLE_TYPES.PUBLIC_GALLERY
      : false;
    return {
      canNetwork: canNetwork,
      canPrivateChat: canNetwork,
      canVideoChat: canNetwork
    };
  },
  /**
   * Check if the current user is admin
   * @return {Object}
   */
  isAdmin: state => {
    return state.user?.is_admin;
  },
  isDomainAdmin: (state, getters, rootState) => domain => {
    return (
      Array.isArray(rootState.event.context.ADMIN_DOMAINS) && rootState.event.context.ADMIN_DOMAINS.includes(domain)
    );
  },
  isCurrentUserDomainAdmin: (state, getters, rootState) => {
    return (
      Array.isArray(rootState.event.context.ADMIN_DOMAINS) &&
      rootState.event.context.ADMIN_DOMAINS.includes(state.domain)
    );
  }
};
